import React from "react";
import { Rocket, Clock, Globe, Settings } from "lucide-react";
import { useDarkMode } from "../../../darkmode/DarkModeContext";

const Elements = () => {
  const { isDarkMode } = useDarkMode();

  const features = [
    {
      icon: <Rocket className="w-8 h-8 text-[#1E9B4C]" />,
      bg: "bg-[#1E9B4C]/20 dark:bg-[#1E9B4C]/30",
      glowColor: "group-hover:shadow-[#1E9B4C]/30",
      borderColor: "border-[#1E9B4C]/30 group-hover:border-[#1E9B4C]/60",
      title: "High-Speed Internet",
      desc: "Kecepatan hingga 250 Mbps untuk streaming & browsing lancar tanpa buffering",
      stat: "250 Mbps",
      delay: "0s",
    },
    {
      icon: <Clock className="w-8 h-8 text-green-400" />,
      bg: "bg-green-600/20 dark:bg-green-900/30",
      glowColor: "group-hover:shadow-green-500/30",
      borderColor: "border-green-500/30 group-hover:border-green-400/60",
      title: "Low Latency",
      desc: "Latency 20–40 ms, ideal untuk gaming online & video conference berkualitas tinggi",
      stat: "20-40 ms",
      delay: "0.1s",
    },
    {
      icon: <Globe className="w-8 h-8 text-purple-400" />,
      bg: "bg-purple-600/20 dark:bg-purple-900/30",
      glowColor: "group-hover:shadow-purple-500/30",
      borderColor: "border-purple-500/30 group-hover:border-purple-400/60",
      title: "Global Coverage",
      desc: "Jangkauan internet luas di seluruh Indonesia & dunia, bahkan di area terpencil",
      stat: "Global",
      delay: "0.2s",
    },
    {
      icon: <Settings className="w-8 h-8 text-orange-400" />,
      bg: "bg-orange-600/20 dark:bg-orange-900/30",
      glowColor: "group-hover:shadow-orange-500/30",
      borderColor: "border-orange-500/30 group-hover:border-orange-400/60",
      title: "Easy Setup",
      desc: "Instalasi mudah & cepat dalam hitungan menit, cukup arahkan dish ke langit",
      stat: "< 5 min",
      delay: "0.3s",
    },
  ];

  return (
    <section
      className={`transition-all duration-500 py-20 relative overflow-hidden ${
        isDarkMode
          ? "bg-[#1E1E1E] text-white"
          : "bg-[#dfe4e0ff] text-gray-900"
      }`}
    >
      {/* Background decorative image */}
      <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16">
          <span
            className={`inline-block mb-4 px-4 py-2 rounded-full text-sm font-medium border ${
              isDarkMode
                ? "bg-[#1E9B4C]/30 text-[#1E9B4C] border-[#1E9B4C]/50"
                : "bg-[#1E9B4C]/10 text-[#1E9B4C] border-[#1E9B4C]/30"
            }`}
          >
            ⚡ Keunggulan Starlink
          </span>
          <h2
            className={`text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent ${
              isDarkMode
                ? "bg-gradient-to-r from-white to-gray-300"
                : "bg-gradient-to-r from-gray-900 to-gray-600"
            }`}
          >
            Kenapa Pilih Starlink?
          </h2>
          <p
            className={`text-lg max-w-2xl mx-auto ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Teknologi satelit terdepan yang memberikan pengalaman internet
            terbaik di kelasnya
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`group relative rounded-2xl p-6 text-center transition-all duration-500 hover:scale-105 border shadow-lg hover:shadow-xl ${
                isDarkMode
                  ? `bg-gray-800/80 border-gray-700/50 ${feature.glowColor}`
                  : `bg-white/80 border-gray-200 ${feature.glowColor}`
              }`}
              style={{
                animation: `fadeInUp 0.6s ease-out ${feature.delay} both`,
              }}
            >
              {/* Floating stat badge */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-[#1E9B4C] to-green-600 text-white text-xs font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                {feature.stat}
              </div>

              {/* Icon */}
              <div className="mb-6">
                <div
                  className={`${feature.bg} w-20 h-20 rounded-2xl flex items-center justify-center mx-auto transition-all duration-300 group-hover:scale-110 group-hover:rotate-6`}
                >
                  {feature.icon}
                </div>
              </div>

              {/* Content */}
              <h3
                className={`text-xl font-bold mb-3 transition-colors ${
                  isDarkMode
                    ? "text-white group-hover:text-[#1E9B4C]"
                    : "text-gray-900 group-hover:text-[#1E9B4C]"
                }`}
              >
                {feature.title}
              </h3>
              <p
                className={`text-sm leading-relaxed transition-colors ${
                  isDarkMode
                    ? "text-gray-300 group-hover:text-gray-200"
                    : "text-gray-600 group-hover:text-gray-700"
                }`}
              >
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default Elements;